"use client"

import { css } from "@emotion/css"
import { useMemo } from "react"
import { VisuallyHidden } from "react-aria"
import { useTranslation } from "react-i18next"

import type { CodeAttributes } from "@/../types/GutenbergBlockAttributes"
import BreakFromCentered from "@/shared-module/common/components/Centering/BreakFromCentered"
import dynamicImport from "@/shared-module/common/utils/dynamicImport"
import { omitUndefined } from "@/shared-module/common/utils/nullability"
import withErrorBoundary from "@/shared-module/common/utils/withErrorBoundary"

import type { BlockRendererProps } from "../../.."
import { CopyButton } from "./CopyButton"
import { parseHighlightedCode } from "./highlightParser"
import { getPreStyles } from "./styles"
import { formatHighlightedLinesRanges, replaceBrTagsWithNewlines } from "./utils"

const SyntaxHighlightedContainer = dynamicImport(() => import("./SyntaxHighlightedContainer"))

const containerStyles = css`
  position: relative;
  max-width: 1000px;
  margin: 0 auto;
`

/**
 * Renders a code block with syntax highlighting and a copy button.
 * Adjusts font size based on the longest line of code.
 *
 * Input (e.g. from Gutenberg) may use `<br>` for line breaks. We normalize those to `\n` before
 * parsing and display. Escaped br (e.g. `&lt;br&gt;`) is left as literal text and is not treated as a newline.
 */
const CodeBlock: React.FC<React.PropsWithChildren<BlockRendererProps<CodeAttributes>>> = ({
  data,
  dontAllowBlockToBeWiderThanContainerWidth,
}) => {
  const { t } = useTranslation()
  // `language` is added to core/code by a CMS block filter and isn't part of the generated CodeAttributes type.
  const { content, language } = data.attributes as CodeAttributes & { language?: string }

  const processedContent = useMemo(() => replaceBrTagsWithNewlines(content ?? undefined), [content])

  const { cleanCode, highlightedLines } = useMemo(
    () => parseHighlightedCode(processedContent),
    [processedContent],
  )

  const fontSizePx = useMemo(() => {
    const longestLine = cleanCode.split("\n").reduce((acc, line) => Math.max(acc, line.length), 0)
    const baseSize = longestLine > 100 ? 14 : longestLine > 70 ? 16 : 20
    if (dontAllowBlockToBeWiderThanContainerWidth) {
      return baseSize - 4
    }
    return baseSize
  }, [cleanCode, dontAllowBlockToBeWiderThanContainerWidth])

  const highlightedLinesSummary =
    highlightedLines && highlightedLines.size > 0
      ? formatHighlightedLinesRanges(highlightedLines)
      : ""

  return (
    <BreakFromCentered sidebar={false}>
      <div className={containerStyles}>
        {highlightedLinesSummary && (
          <VisuallyHidden>
            {t("code-block-highlighted-lines", { lines: highlightedLinesSummary })}
          </VisuallyHidden>
        )}
        {cleanCode && <CopyButton content={cleanCode} />}
        <pre
          className={getPreStyles(fontSizePx, dontAllowBlockToBeWiderThanContainerWidth ?? false)}
        >
          <SyntaxHighlightedContainer
            content={cleanCode}
            highlightedLines={highlightedLines}
            {...omitUndefined({ language })}
          />
        </pre>
      </div>
    </BreakFromCentered>
  )
}

export default withErrorBoundary(CodeBlock)
